import { PLACEHOLDER_EVENT_DATE } from './constants';
import type { EventRecord, PublishedEvent, SchoolTerm } from './types';

export const formatTermLabel = (term: SchoolTerm): string => `Term ${term}`;

export const formatEventDisplayDate = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00`);
  return parsed.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/** Normalise dates from published/admin records (supports legacy single `date`). */
export const getEventDates = (
  event:
    | Pick<PublishedEvent, 'dates' | 'date'>
    | Pick<EventRecord, 'dates' | 'date'>,
): string[] => {
  if (Array.isArray(event.dates) && event.dates.length > 0) {
    return [...event.dates]
      .filter((value) => value && value !== PLACEHOLDER_EVENT_DATE)
      .sort((a, b) => a.localeCompare(b));
  }
  if (event.date && event.date !== PLACEHOLDER_EVENT_DATE) {
    return [event.date];
  }
  return [];
};

export const getEarliestEventDate = (
  event:
    | Pick<PublishedEvent, 'dates' | 'date'>
    | Pick<EventRecord, 'dates' | 'date'>,
): string | undefined => getEventDates(event)[0];

export const getLatestEventDate = (
  event:
    | Pick<PublishedEvent, 'dates' | 'date'>
    | Pick<EventRecord, 'dates' | 'date'>,
): string | undefined => {
  const dates = getEventDates(event);
  return dates[dates.length - 1];
};

export const formatEventDatesLabel = (
  event: Pick<PublishedEvent, 'dates' | 'date'>,
): string => {
  const dates = getEventDates(event);
  if (dates.length === 0) {
    return '';
  }
  return dates.map(formatEventDisplayDate).join(' · ');
};

export const formatEventTileDate = (event: PublishedEvent): string => {
  const dateLabel = formatEventDatesLabel(event);
  const termLabel = formatTermLabel(event.term);
  return dateLabel ? `${dateLabel} · ${termLabel}` : termLabel;
};

export const getTodayIsoDate = (): string =>
  new Date().toISOString().slice(0, 10);

export const isEventUpcoming = (
  event: Pick<PublishedEvent, 'dates' | 'date'>,
  today = getTodayIsoDate(),
): boolean => {
  const latest = getLatestEventDate(event);
  return Boolean(latest && latest >= today);
};

export const splitEventsByDate = (
  events: PublishedEvent[],
  today = getTodayIsoDate(),
): {
  upcoming: PublishedEvent[];
  past: PublishedEvent[];
  highlighted: PublishedEvent | null;
} => {
  const sorted = [...events].sort((a, b) => {
    const aDate = getEarliestEventDate(a) ?? '';
    const bDate = getEarliestEventDate(b) ?? '';
    return aDate.localeCompare(bDate);
  });

  const upcoming = sorted.filter(
    (event) => isEventUpcoming(event, today) && !event.highlight,
  );
  const past = sorted.filter((event) => !isEventUpcoming(event, today));
  const highlighted =
    sorted.find((event) => event.highlight && isEventUpcoming(event, today)) ??
    null;

  return { upcoming, past, highlighted };
};

export const splitEventsForCalendar = (
  events: PublishedEvent[],
  today = getTodayIsoDate(),
): {
  upcoming: PublishedEvent[];
  past: PublishedEvent[];
} => {
  const upcoming = events
    .filter((event) => isEventUpcoming(event, today))
    .sort((a, b) => {
      const aDate = getEarliestEventDate(a) ?? '';
      const bDate = getEarliestEventDate(b) ?? '';
      return aDate.localeCompare(bDate);
    });
  const past = events
    .filter((event) => !isEventUpcoming(event, today))
    .sort((a, b) => {
      const aDate = getLatestEventDate(a) ?? '';
      const bDate = getLatestEventDate(b) ?? '';
      return bDate.localeCompare(aDate);
    });

  return { upcoming, past };
};

export const renderDescriptionParagraphs = (description: string): string[] =>
  description.split(/\n+/).filter((line) => line.trim().length > 0);
